import { useState } from 'react';
import { Card, CardBody, Switch, Skeleton } from '@vnetwork/ui/primitives';
import { SettingRow } from '@vnetwork/ui/layout';
import { ConfirmDialog, useToast } from '@vnetwork/ui/feedback';
import { useWaapProtection, useWaapSetProtection } from '@/api/waap';
import { errMessage } from '@/pages/services/waap/waap-util';

/**
 * WAAP protection master toggle for one website — the reversible replacement for
 * the old destructive "Remove Security" action. Reads `waap_enabled` and flips it
 * via PUT /security (the vendor keeps the site config, so it can be turned back
 * on any time). Rendered as the app-wide SettingRow toggle pattern; disabling
 * asks for confirmation because it stops traffic inspection.
 */
export function WebsiteProtectionCard({ domainId }: { domainId: string }) {
  const { show } = useToast();
  const { data, isLoading } = useWaapProtection(domainId);
  const [confirmOff, setConfirmOff] = useState(false);
  const enabled = !!data?.enabled;

  const setProtection = useWaapSetProtection(domainId, {
    onSuccess: (_r, vars) => {
      setConfirmOff(false);
      show({ tone: 'success', title: vars.enabled ? 'Protection enabled' : 'Protection disabled' });
    },
    onError: (e) =>
      show({ tone: 'danger', title: 'Could not update protection', description: errMessage(e) }),
  });

  return (
    <Card>
      <CardBody>
        {isLoading ? (
          <Skeleton style={{ height: 48 }} />
        ) : (
          <SettingRow
            title="WAAP protection"
            description={
              enabled
                ? 'This website is protected — WAAP inspects its traffic.'
                : 'Protection is off — WAAP is not inspecting this website’s traffic.'
            }
            control={
              <Switch
                checked={enabled}
                disabled={setProtection.isPending}
                onCheckedChange={(v) => (v ? setProtection.mutate({ enabled: true }) : setConfirmOff(true))}
              />
            }
          />
        )}
      </CardBody>

      <ConfirmDialog
        open={confirmOff}
        onOpenChange={(o) => !o && setConfirmOff(false)}
        title="Disable protection?"
        description="WAAP will stop inspecting traffic for this website. Its security config is kept — you can re-enable protection any time."
        confirmLabel="Disable"
        destructive
        loading={setProtection.isPending}
        onConfirm={() => setProtection.mutate({ enabled: false })}
      />
    </Card>
  );
}
